import React, { useMemo, useState, useEffect, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
  Box,
  Typography,
  CardMedia,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { MRT_Localization_ES } from "material-react-table/locales/es"
import CustomPhotoCamV2 from "../../../../components/CustomPhotoCamV2"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import CustomBackdrop from "../../../../components/CustomBackdrop"

const ProductImagesModal = ({ open, onClose, artcodigo, invcodigo }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [data, setData] = useState([])
  const [rowSelection, setRowSelection] = useState({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [editingRow, setEditingRow] = useState(null)
  const [hasPermissionImageTab, setHasPermissionImageTab] = useState(false)
  const [isLoadingGetImgs, setIsLoadingGetImgs] = useState(false)
  const [isLoadingSaveImg, setIsLoadingSaveImg] = useState(false)

  // Imagen actual del carrusel
  const currentImage = data[currentIndex]

  // Imagen inicial que se pasa al CustomPhotoCamV2
  const initialImageForCam = useMemo(() => {
    if (editingRow && editingRow.artimagen) {
      return `data:image/jpeg;base64,${editingRow.artimagen}`
    }
    return null
  }, [editingRow])

  // ------------------- GET -------------------
  const getAllImages = async () => {
    try {
      setIsLoadingGetImgs(true)
      const options = {
        method: "POST",
        body: JSON.stringify({ artcodigo }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }

      let response = await fetchwrapper("/productos/getListImages", options)
      response = await response.json()
      response = response.data
      setHasPermissionImageTab(response.hasPermission)
      setData(response.images)
      setCurrentIndex((prev) => Math.min(prev, Math.max(response.images.length - 1, 0)))
    } catch (error) {
      alert("Error al cargar la galería de imágenes")
    } finally {
      setIsLoadingGetImgs(false)
    }
  }

  useEffect(() => {
    if (open) {
      getAllImages()
      setRowSelection({})
      setCurrentIndex(0)
    }
  }, [open, artcodigo])

  // ------------------- CRUD -------------------
  const createNewImage = async (artimagen) => {
    try {
      setIsLoadingSaveImg(true)
      const options = {
        method: "POST",
        body: JSON.stringify({ artcodigo, invcodigo, artimagen }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
      await fetchwrapper("/productos/addNewImage", options)
      await getAllImages()
    } catch (error) {
      alert("Error al agregar una nueva imagen")
    } finally {
      setIsLoadingSaveImg(false)
    }
  }

  const editOldImage = async (artimagen, artsecuen) => {
    try {
      setIsLoadingSaveImg(true)
      const options = {
        method: "POST",
        body: JSON.stringify({ artcodigo, invcodigo, artsecuen, artimagen }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
      await fetchwrapper("/productos/editSpecificImage", options)
      await getAllImages()
    } catch (error) {
      alert("Error al editar la imagen")
    } finally {
      setIsLoadingSaveImg(false)
      setIsCameraOpen(false)
      setEditingRow(null)
      setRowSelection({})
    }
  }

  const deleteImage = async () => {
    const artsecuen = parseInt(Object.keys(rowSelection)[0], 10)
    try {
      setIsLoadingSaveImg(true)
      const options = {
        method: "POST",
        body: JSON.stringify({ artcodigo, invcodigo, artsecuen }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
      await fetchwrapper("/productos/deleteSpecificImage", options)
      await getAllImages()
      setRowSelection({})
    } catch (error) {
      alert("Error al eliminar la imagen")
    } finally {
      setIsLoadingSaveImg(false)
    }
  }

  // ------------------- CALLBACK DE LA CÁMARA -------------------
  const handleImageFromCam = async (img) => {
    if (!img || !img.base64Hex) return

    if (editingRow) {
      await editOldImage(img.base64Hex, editingRow.artsecuen)
    } else {
      await createNewImage(img.base64Hex)
      setIsCameraOpen(false)
    }
  }

  // ------------------- BOTONES -------------------
  const handleOpenCameraNew = () => {
    setEditingRow(null)
    setIsCameraOpen(true)
  }

  const handleOpenCameraEdit = () => {
    const selectedRowKey = Object.keys(rowSelection)[0]
    if (!selectedRowKey) return
    const row = data.find((d) => String(d.artsecuen) === String(selectedRowKey))
    setEditingRow(row)
    setIsCameraOpen(true)
  }

  // ------------------- CARRUSEL -------------------
  const handlePrev = () => setCurrentIndex((i) => (i - 1 + data.length) % data.length)
  const handleNext = () => setCurrentIndex((i) => (i + 1) % data.length)

  // ------------------- TABLA -------------------
  const columns = useMemo(
    () => [
      { accessorKey: "artsecuen", header: "Secuencia" },
      {
        accessorKey: "artimagen",
        header: "Imagen",
        Cell: ({ cell }) => (
          <img src={`data:image/jpeg;base64,${cell.getValue()}`} alt="Imagen" style={{ width: 100 }} />
        ),
      },
    ],
    [],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    getRowId: (row) => String(row.artsecuen),
    enableRowSelection: true,
    enableMultiRowSelection: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
    localization: { ...MRT_Localization_ES },
    enableTopToolbar: false,
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
  })

  // ------------------- RENDER -------------------
  return (
    <>
      <CustomBackdrop isLoading={isLoadingGetImgs || isLoadingSaveImg} zIndex={999999} />

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        fullScreen={isMobile}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            // 95% de la pantalla
            width: { xs: "100%", sm: "95vw" },
            height: { xs: "100%", sm: "95vh" },
            maxWidth: "95vw",
            maxHeight: "95vh",
            m: { xs: 0, sm: "auto" },
            display: "flex",
            flexDirection: "column",
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" noWrap>
            Imágenes del artículo {artcodigo}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          {!hasPermissionImageTab ? (
            <Typography>Usted no tiene permiso a esta vista</Typography>
          ) : (
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
              {/* ---------- CARRUSEL ---------- */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 220, sm: 320 },
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {currentImage ? (
                  <>
                    <CardMedia
                      component="img"
                      image={`data:image/jpeg;base64,${currentImage.artimagen}`}
                      alt={`Imagen ${currentIndex + 1}`}
                      sx={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                    />
                    {data.length > 1 && (
                      <>
                        <IconButton
                          onClick={handlePrev}
                          sx={{
                            position: "absolute",
                            left: 8,
                            bgcolor: "rgba(0,0,0,0.4)",
                            color: "white",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                          }}
                        >
                          <ArrowBackIosNewIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleNext}
                          sx={{
                            position: "absolute",
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.4)",
                            color: "white",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                          }}
                        >
                          <ArrowForwardIosIcon />
                        </IconButton>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "white",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: 12,
                          }}
                        >
                          {currentIndex + 1} / {data.length}
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">Sin imágenes</Typography>
                )}
              </Box>

              {/* ---------- BOTONES (verticales en móvil, horizontales en desktop) ---------- */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="center"
                alignItems="stretch"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CameraAltIcon />}
                  onClick={handleOpenCameraNew}
                  fullWidth={isMobile}
                >
                  Capturar foto
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={handleOpenCameraEdit}
                  disabled={Object.keys(rowSelection).length === 0}
                  fullWidth={isMobile}
                >
                  Editar seleccionada
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={deleteImage}
                  disabled={Object.keys(rowSelection).length === 0}
                  fullWidth={isMobile}
                >
                  Eliminar seleccionada
                </Button>
              </Stack>

              {/* ---------- TABLA ---------- */}
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <MaterialReactTable table={table} />
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- MODAL DE CÁMARA ---------- */}
      {isCameraOpen && (
        <CustomPhotoCamV2
          open={isCameraOpen}
          onClose={() => {
            setIsCameraOpen(false)
            setEditingRow(null)
          }}
          label={editingRow ? "Editar imagen" : "Capturar nueva imagen"}
          onImage={handleImageFromCam}
          initialImage={initialImageForCam}
          cropAspect={4 / 3}
          compressOptions={{ maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true }}
          maxFileSizeMB={5}
          allowedFileTypes={["image/jpeg", "image/png", "image/webp"]}
          showUploadButton={true}
        />
      )}
    </>
  )
}

export default ProductImagesModal
